/**
 * Draft Manager Service
 * Manages drafts per module in the Admin Portal.
 * Enforces a maximum of 3 drafts per module.
 */

const DRAFT_PREFIX = 'gpn_module_drafts_';
const MAX_DRAFTS = 5;

/**
 * Determine active organization ID from storage if not explicitly provided
 */
const getActiveOrgId = () => {
  try {
    const rawMode = sessionStorage.getItem('gpn_org_view_mode');
    if (rawMode) {
      const mode = JSON.parse(rawMode);
      if (mode?.orgId) return String(mode.orgId);
    }
    const rawUser = localStorage.getItem('userInfo');
    if (rawUser) {
      const user = JSON.parse(rawUser);
      if (user.is_switched_from_super_admin && user.org_id) return String(user.org_id);
      if (user.role !== 1 && user.org_id) return String(user.org_id);
    }
  } catch (e) {
    console.error('Error resolving active org for drafts', e);
  }
  return 'global';
};

export const DraftManager = {
  /**
   * Compute localStorage key scoped by organization
   */
  getStorageKey: (moduleKey, orgId = null) => {
    const effectiveOrg = orgId !== null && orgId !== undefined && orgId !== ''
      ? String(orgId)
      : getActiveOrgId();
    return `${DRAFT_PREFIX}${moduleKey}_${effectiveOrg}`;
  },

  /**
   * Get all drafts for a given module and organization
   */
  getDrafts: (moduleKey, orgId = null) => {
    try {
      const key = DraftManager.getStorageKey(moduleKey, orgId);
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const drafts = JSON.parse(raw);
      return Array.isArray(drafts) ? drafts : [];
    } catch (e) {
      console.error('Error reading drafts for', moduleKey, e);
      return [];
    }
  },

  /**
   * Get draft count for a given module and organization
   */
  getDraftCount: (moduleKey, orgId = null) => {
    try {
      const drafts = DraftManager.getDrafts(moduleKey, orgId);
      return Array.isArray(drafts) ? drafts.length : 0;
    } catch (e) {
      return 0;
    }
  },

  /**
   * Save or update a draft scoped by organization.
   * Enforces maximum 3 drafts per organization.
   */
  saveDraft: (moduleKey, data, existingDraftId = null, orgId = null) => {
    try {
      const key = DraftManager.getStorageKey(moduleKey, orgId);
      const effectiveOrg = orgId !== null && orgId !== undefined && orgId !== ''
        ? String(orgId)
        : getActiveOrgId();
      const drafts = DraftManager.getDrafts(moduleKey, effectiveOrg);

      // Check if updating existing draft
      if (existingDraftId) {
        const index = drafts.findIndex((d) => d.id === existingDraftId);
        if (index !== -1) {
          drafts[index] = {
            ...drafts[index],
            data,
            title: data.title || data.name || drafts[index].title || 'Untitled Draft',
            summary: data.description || data.requestText || data.body || '',
            orgId: effectiveOrg,
            updatedAt: new Date().toISOString(),
          };
          localStorage.setItem(key, JSON.stringify(drafts));
          return { success: true, draftId: existingDraftId, drafts };
        }
      }

      // Check if the most recent draft can be updated (e.g. same title or both untitled within 5 minutes)
      const currentTitle = data.title || data.name || data.prayerTitle || '';
      if (!existingDraftId && drafts.length > 0) {
        const first = drafts[0];
        const isSameTitle = currentTitle && first.title === currentTitle;
        const bothUntitled = (!currentTitle || currentTitle === 'Untitled Draft') && (!first.title || first.title === 'Untitled Draft');
        const isRecent = (Date.now() - new Date(first.updatedAt || first.createdAt).getTime()) < 5 * 60 * 1000;

        if ((isSameTitle || bothUntitled) && isRecent) {
          drafts[0] = {
            ...first,
            data,
            title: currentTitle || first.title || 'Untitled Draft',
            summary: data.description || data.requestText || data.body || '',
            orgId: effectiveOrg,
            updatedAt: new Date().toISOString(),
          };
          localStorage.setItem(key, JSON.stringify(drafts));
          return { success: true, draftId: first.id, drafts };
        }
      }

      // If maximum drafts reached, automatically prune the oldest draft so saving never fails
      let trimmedDrafts = drafts;
      if (trimmedDrafts.length >= MAX_DRAFTS) {
        trimmedDrafts = trimmedDrafts.slice(0, MAX_DRAFTS - 1);
      }

      // Create new draft
      const newDraft = {
        id: `draft_${Date.now()}`,
        title: currentTitle || 'Untitled Draft',
        summary: data.description || data.requestText || data.body || '',
        data,
        orgId: effectiveOrg,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const updatedDrafts = [newDraft, ...trimmedDrafts];
      localStorage.setItem(key, JSON.stringify(updatedDrafts));
      return { success: true, draftId: newDraft.id, drafts: updatedDrafts };
    } catch (e) {
      console.error('Error saving draft:', e);
      return { success: false, message: 'Failed to save draft' };
    }
  },

  /**
   * Delete a draft by ID
   */
  deleteDraft: (moduleKey, draftId, orgId = null) => {
    try {
      const key = DraftManager.getStorageKey(moduleKey, orgId);
      const drafts = DraftManager.getDrafts(moduleKey, orgId);
      const filtered = drafts.filter((d) => d.id !== draftId);
      localStorage.setItem(key, JSON.stringify(filtered));
      return { success: true, drafts: filtered };
    } catch (e) {
      console.error('Error deleting draft:', e);
      return { success: false, message: 'Failed to delete draft' };
    }
  },

  /**
   * Clear all drafts for a module and organization
   */
  clearAllDrafts: (moduleKey, orgId = null) => {
    try {
      const key = DraftManager.getStorageKey(moduleKey, orgId);
      localStorage.removeItem(key);
      return { success: true, drafts: [] };
    } catch (e) {
      console.error('Error clearing drafts:', e);
      return { success: false };
    }
  },
};

export default DraftManager;
